import React from 'react'
import propTypes from 'prop-types'
import { observer } from 'mobx-react'
import { Field, FieldArray } from 'formik'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import get from 'lodash/get'

import RepeatableAction from '../RepeatableAction'
import RichTextEditor from '../RichTextEditor'

const createEmptyObject = () => ({ question: '' })

const getItemStyle = (draggableStyle, isDragging) => {
  return {
    userSelect: 'none',
    border: 'none',
    display: 'flex',
    boxShadow: isDragging ? '2px 2px 25px -10px black' : '',
    ...draggableStyle
  }
}

const getListStyle = isDraggingOver => ({
  transition: 'background 0.2s ease-out',
  background: isDraggingOver ? 'rgba(0, 149, 220, 0.1)' : 'inherit'
})

const ScreeningQuestionFields = ({
  form,
  name,
  label,
  questionRichTextEditorProps,
  screeningQuestionsNotRepeating
}) => {
  let screeningQuestions = get(form.values, name)
  if (screeningQuestions.length === 0) {
    screeningQuestions = [createEmptyObject()]
  }

  const onDragEnd = (result, form) => {
    const { source, destination } = result
    if (!destination) return

    const [removed] = screeningQuestions.splice(source.index, 1)
    screeningQuestions.splice(destination.index, 0, removed)

    form.setFieldTouched(`screeningQuestions[${source.index}].question`, true)
    form.setFieldTouched(
      `screeningQuestions[${destination.index}].question`,
      true
    )
  }

  const inputs = (
    <div className='expert-form__input-wrapper'>
      <DragDropContext
        onDragEnd={result => {
          onDragEnd(result, form)
        }}
      >
        <Droppable droppableId='droppable'>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              <FieldArray
                name={name}
                render={arrayHelpers => (
                  <div className='expert-form__repeatable-input-wrapper'>
                    {screeningQuestions.map((q, index) => {
                      const questionComponent = (
                        <Draggable
                          index={index}
                          key={index}
                          draggableId={`ala-${index}`}
                        >
                          {(provided, snapshot) => {
                            const classes = snapshot.isDragging
                              ? 'epl__draggable epl__draggable--dragging'
                              : 'epl__draggable'
                            return (
                              <div>
                                <div
                                  ref={provided.innerRef}
                                  style={getItemStyle(
                                    provided.draggableProps.style,
                                    snapshot.isDragging
                                  )}
                                  className={classes}
                                  {...provided.dragHandleProps}
                                >
                                  <Field
                                    name={`${name}[${index}].question`}
                                    placeholder='Question'
                                    value={screeningQuestions[index].question}
                                    component={RichTextEditor}
                                    skipWrapper
                                    {...questionRichTextEditorProps}
                                  />
                                  <div className='drag-area'>
                                    Click here to drag
                                  </div>
                                </div>
                                {provided.placeholder}
                              </div>
                            )
                          }}
                        </Draggable>
                      )
                      const repeatableActionComponent = (
                        <RepeatableAction
                          fieldValues={screeningQuestions}
                          arrayHelpers={arrayHelpers}
                          index={index}
                          initialObject={createEmptyObject()}
                        />
                      )

                      return (
                        <div
                          className='expert-form__repeatable-input expert-form__repeatable-input--segments'
                          key={index}
                        >
                          {questionComponent}
                          {repeatableActionComponent}
                        </div>
                      )
                    })}
                  </div>
                )}
              />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )

  if (!label) {
    return inputs
  }

  return (
    <div className='expert-form__input-group expert-form__input-group--no-flex'>
      <label className='code-label m-b-md'>{label}</label>
      {inputs}
      {screeningQuestionsNotRepeating ? (
        <div className='expert-form__input-error'>
          {screeningQuestionsNotRepeating}
        </div>
      ) : null}
    </div>
  )
}

ScreeningQuestionFields.propTypes = {
  form: propTypes.object,
  name: propTypes.oneOfType([propTypes.string, propTypes.array]),
  label: propTypes.string,
  questionRichTextEditorProps: propTypes.object,
  screeningQuestionsNotRepeating: propTypes.string
}

export default observer(ScreeningQuestionFields)
